import { Asset } from '@/lib/types'
import { nanoid } from 'nanoid'
interface IAsset {
  link: string
  thumbnail: string
}
/**
 * API calling for starting AI coding
 * @param prompt - The user's prompt for the project
 * @param promptForAi - Optional AI-specific prompt
 * @param baseTemplateId - Optional base template ID
 * @param assets - Array of assets to include in the project
 * @param additionalFields - Additional fields including workspace_id, entry_point, etc.
 * @returns Project ID
 */
export const startAiPlanApi = async (
  prompt: string,
  promptForAi: string | null | undefined,
  baseTemplateId: string | null | undefined,
  assets: IAsset[] | Asset[],
  additionalFields?: {
    entry_point?: string
    workspace_id?: string
    [key: string]: any
  },
) => {
  const requestBody = {
    prompt: prompt,
    ...(promptForAi && { promptForAi }),
    baseTemplateId,
    assets: JSON.stringify(assets),
    firstUserMessageContent: prompt,
    ...additionalFields,
  }

  // An uncertain result keeps its request ID through retries and page refresh.
  // Store only a fingerprint, never the prompt or asset data, as the key.
  const serializedBody = JSON.stringify(requestBody)
  let first = 2166136261
  let second = 5381
  for (let index = 0; index < serializedBody.length; index++) {
    const code = serializedBody.charCodeAt(index)
    first = Math.imul(first ^ code, 16777619)
    second = Math.imul(second, 33) ^ code
  }
  const pendingKey = `heyboss-project-create:${(first >>> 0).toString(16)}:${(second >>> 0).toString(16)}`
  const requestId = sessionStorage.getItem(pendingKey) || nanoid(12)
  sessionStorage.setItem(pendingKey, requestId)
  const send = () => fetch('/api/me/projects', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': requestId,
    },
    body: serializedBody,
  })
  for (let attempt = 0; ; attempt++) {
    let response: Response | undefined
    try {
      response = await send()
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const error = new Error(errorData.error || 'Failed to create project')
        ;(error as any).key = errorData.key
        ;(error as any).response = errorData
        ;(error as any).status = response.status
        throw error
      }
      const { _id } = await response.json()
      if (typeof _id !== 'string' || !_id) {
        throw new Error('Project creation response has no ID')
      }
      sessionStorage.removeItem(pendingKey)
      return _id
    } catch (error) {
      if (response && !response.ok && response.status < 500) {
        sessionStorage.removeItem(pendingKey)
        throw error
      }
      if (attempt >= 2) throw error
      await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)))
    }
  }
}
